import { useEffect, useState } from 'react';
import DataTable, { TableColumn } from 'react-data-table-component';
import api from '../services/api';
import { Button, Modal, Badge, TableActionButtons, TableAction, EditIcon, DeleteIcon, InactivateIcon, ActivateIcon, DataTablePagination, DataTableCard } from '../components/ui';
import { DATA_TABLE_THEME, dataTableCustomStyles } from '../utils/dataTableTheme';

const formInputClass = 'w-full bg-rs-surface border border-rs-border rounded-lg text-gray-100 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-rs-accent/50 placeholder-gray-500';
const formLabelClass = 'block text-sm font-medium text-gray-300 mb-2';

interface EditState {
  open: boolean;
  license: License | null;
  saving: boolean;
  error: string;
}

interface CreateState {
  open: boolean;
  saving: boolean;
  error: string;
  license: Partial<License>;
}

interface License {
  id: string;
  license_key: string;
  ip: string;
  port: number;
  status: string;
  expires_at: string;
  user_id?: string;
  product_id?: string;
}

interface ProductItem {
  id: number | string;
  name: string;
}

interface UserItem {
  id: string;
  username: string;
  email: string;
}

export default function Licenses() {
  const userStr = localStorage.getItem('user');
  let user: { role?: string } | null = null;
  try { user = userStr ? JSON.parse(userStr) : null; } catch { user = null; }
  const role = String(user?.role || '').toLowerCase();
  const isAdmin = ['admin', 'owner', 'dueno', 'dueño'].includes(role);

  const [licenses, setLicenses] = useState<License[]>([]);
  const [error, setError] = useState('');
  const [pending, setPending] = useState(true);
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [users, setUsers] = useState<UserItem[]>([]);
  const [edit, setEdit] = useState<EditState>({ open: false, license: null, saving: false, error: '' });
  const [create, setCreate] = useState<CreateState>({ open: false, saving: false, error: '', license: { status: 'active' } });
  const handleCreateOpen = () => setCreate({ open: true, saving: false, error: '', license: { status: 'active' } });
  const handleCreateClose = () => setCreate({ open: false, saving: false, error: '', license: { status: 'active' } });
  const handleCreateChange = (field: keyof License, value: any) => setCreate(c => ({ ...c, license: { ...c.license, [field]: value } }));
  const handleCreateSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreate(c => ({ ...c, saving: true, error: '' }));
    try {
      const payload = {
        ip: create.license.ip,
        port: create.license.port,
        status: create.license.status,
        expires_at: create.license.expires_at ? String(create.license.expires_at).split('T')[0] : null,
        user_id: create.license.user_id,
        product_id: create.license.product_id
      };
      await api.post('/admin/licenses', payload);
      setCreate({ open: false, saving: false, error: '', license: { status: 'active' } });
      fetchLicenses();
    } catch {
      setCreate(c => ({ ...c, saving: false, error: 'Error al crear licencia' }));
    }
  };

  const fetchLicenses = () => {
    setPending(true);
    api.get('/admin/licenses')
      .then(res => setLicenses(res.data))
      .catch(() => setError('Error al cargar licencias'))
      .finally(() => setPending(false));
  };

  useEffect(() => {
    fetchLicenses();
    // fetch products and users for selection
    api.get('/admin/products').then(r => setProducts(r.data)).catch(() => {});
    api.get('/admin/users').then(r => setUsers(r.data)).catch(() => {});
  }, []);

  const handleEdit = (row: License) => {
    setEdit({ open: true, license: { ...row }, saving: false, error: '' });
  };
  const handleDelete = async (row: License) => {
    if (!window.confirm('¿Seguro que deseas eliminar esta licencia?')) return;
    try {
      await api.delete(`/admin/licenses/${row.id}`);
      fetchLicenses();
    } catch {
      alert('Error al eliminar licencia');
    }
  };
  const handleInactivate = async (row: License) => {
    try {
      const { ip, port, expires_at } = row;
      await api.put(`/admin/licenses/${row.id}`, {
        ip,
        port,
        status: 'suspended',
        expires_at: expires_at ? expires_at.split('T')[0] : null
      });
      fetchLicenses();
    } catch {
      alert('Error al inactivar licencia');
    }
  };

  const handleActivate = async (row: License) => {
    try {
      const { ip, port, expires_at } = row;
      await api.put(`/admin/licenses/${row.id}`, {
        ip,
        port,
        status: 'active',
        expires_at: expires_at ? expires_at.split('T')[0] : null
      });
      fetchLicenses();
    } catch {
      alert('Error al activar licencia');
    }
  };

  const statusBadge = (s: string) => {
    const v: 'success' | 'warning' | 'default' = s === 'active' ? 'success' : s === 'suspended' ? 'warning' : 'default';
    return <Badge variant={v}>{s}</Badge>;
  };

  const handleEditSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!edit.license) return;
    setEdit(edit => ({ ...edit, saving: true, error: '' }));
    try {
      const { ip, port, status, expires_at } = edit.license;
      const payload = {
        ip,
        port,
        status,
        expires_at: expires_at ? expires_at.split('T')[0] : null
      };
      await api.put(`/admin/licenses/${edit.license.id}`, payload);
      setEdit({ open: false, license: null, saving: false, error: '' });
      fetchLicenses();
    } catch {
      setEdit(edit => ({ ...edit, saving: false, error: 'Error al guardar cambios' }));
    }
  };

  const baseColumns: TableColumn<License>[] = [
    { name: 'License Key', selector: row => row.license_key, sortable: true },
    { name: 'IP', selector: row => row.ip, sortable: true },
    { name: 'Puerto', selector: row => row.port, sortable: true },
    { name: 'Status', cell: row => statusBadge(row.status), sortable: true },
    { name: 'Expira', selector: row => row.expires_at ? row.expires_at.split('T')[0] : '-', sortable: true },
  ];
  const adminColumns: TableColumn<License>[] = isAdmin ? [
    { name: 'User', selector: row => row.user_id || '-', sortable: true },
    { name: 'Producto', selector: row => row.product_id || '-', sortable: true },
    {
      name: 'Acciones',
      cell: row => {
        const actions: TableAction[] = [
          { label: 'Editar', icon: <EditIcon />, variant: 'edit', onClick: () => handleEdit(row) },
          { label: 'Eliminar', icon: <DeleteIcon />, variant: 'delete', onClick: () => handleDelete(row) },
        ];
        if (row.status === 'suspended') {
          actions.push({ label: 'Activar', icon: <ActivateIcon />, variant: 'secondary', onClick: () => handleActivate(row) });
        } else {
          actions.push({ label: 'Inactivar', icon: <InactivateIcon />, variant: 'secondary', onClick: () => handleInactivate(row), disabled: row.status === 'expired' });
        }
        return <TableActionButtons actions={actions} />;
      },
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ] : [];
  const columns = [...baseColumns, ...adminColumns];

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-2xl font-bold text-gray-100">{isAdmin ? 'Licencias' : 'Mis licencias'}</h1>
        {isAdmin && (
          <Button onClick={handleCreateOpen} leftIcon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>}>
            Nueva licencia
          </Button>
        )}
      </div>
      {error && <div className="text-red-500 mb-2">{error}</div>}
      <DataTableCard>
      <DataTable
        columns={columns}
        data={licenses}
        theme={DATA_TABLE_THEME}
        customStyles={dataTableCustomStyles}
        paginationComponent={DataTablePagination}
        progressPending={pending}
        pagination
        highlightOnHover
        responsive
        noDataComponent={<div className="py-12 text-center text-gray-500">No hay licencias registradas</div>}
      />
      </DataTableCard>

      {isAdmin && <Modal isOpen={create.open} onClose={handleCreateClose} title="Nueva licencia">
        <form onSubmit={handleCreateSave} className="space-y-4">
          <div>
            <label className={formLabelClass}>IP</label>
            <input className={formInputClass} value={create.license.ip || ''} onChange={e => handleCreateChange('ip', e.target.value)} required />
          </div>
          <div>
            <label className={formLabelClass}>Puerto</label>
            <input type="number" className={formInputClass} value={create.license.port || ''} onChange={e => handleCreateChange('port', Number(e.target.value))} required />
          </div>
          <div>
            <label className={formLabelClass}>Usuario</label>
            <select className={formInputClass} value={create.license.user_id || ''} onChange={e => handleCreateChange('user_id', e.target.value)} required>
              <option value="">Seleccione usuario</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.username} ({u.email})</option>)}
            </select>
          </div>
          <div>
            <label className={formLabelClass}>Producto</label>
            <select className={formInputClass} value={create.license.product_id || ''} onChange={e => handleCreateChange('product_id', e.target.value)} required>
              <option value="">Seleccione producto</option>
              {products.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className={formLabelClass}>Estado</label>
            <select className={formInputClass} value={create.license.status || 'active'} onChange={e => handleCreateChange('status', e.target.value)}>
              <option value="active">Activa</option>
              <option value="suspended">Suspendida</option>
              <option value="expired">Expirada</option>
            </select>
          </div>
          <div>
            <label className={formLabelClass}>Expira</label>
            <input type="date" className={formInputClass} value={create.license.expires_at ? String(create.license.expires_at).split('T')[0] : ''} onChange={e => handleCreateChange('expires_at', e.target.value)} />
          </div>
          {create.error && <p className="text-red-400 text-sm">{create.error}</p>}
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="ghost" fullWidth onClick={handleCreateClose}>Cancelar</Button>
            <Button type="submit" fullWidth loading={create.saving}>Crear licencia</Button>
          </div>
        </form>
      </Modal>}

      {isAdmin && <Modal isOpen={edit.open} onClose={() => setEdit({ open: false, license: null, saving: false, error: '' })} title="Editar licencia">
        {edit.license && (
          <form onSubmit={handleEditSave} className="space-y-4">
            <div>
              <label className={formLabelClass}>License Key</label>
              <input className={formInputClass + ' bg-rs-surfaceLight cursor-not-allowed opacity-80'} value={edit.license.license_key} readOnly disabled />
            </div>
            <div>
              <label className={formLabelClass}>IP</label>
              <input className={formInputClass} value={edit.license.ip} onChange={e => setEdit(edit => ({ ...edit, license: { ...edit.license!, ip: e.target.value } }))} required />
            </div>
            <div>
              <label className={formLabelClass}>Puerto</label>
              <input type="number" className={formInputClass} value={edit.license.port} onChange={e => setEdit(edit => ({ ...edit, license: { ...edit.license!, port: Number(e.target.value) } }))} required />
            </div>
            <div>
              <label className={formLabelClass}>Estado</label>
              <select className={formInputClass} value={edit.license.status} onChange={e => setEdit(edit => ({ ...edit, license: { ...edit.license!, status: e.target.value } }))}>
                <option value="active">Activa</option>
                <option value="suspended">Suspendida</option>
                <option value="expired">Expirada</option>
              </select>
            </div>
            <div>
              <label className={formLabelClass}>Expira</label>
              <input type="date" className={formInputClass} value={edit.license.expires_at ? edit.license.expires_at.split('T')[0] : ''} onChange={e => setEdit(edit => ({ ...edit, license: { ...edit.license!, expires_at: e.target.value } }))} />
            </div>
            {edit.error && <p className="text-red-400 text-sm">{edit.error}</p>}
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="ghost" fullWidth onClick={() => setEdit({ open: false, license: null, saving: false, error: '' })}>Cancelar</Button>
              <Button type="submit" variant="secondary" fullWidth loading={edit.saving}>Guardar cambios</Button>
            </div>
          </form>
        )}
      </Modal>}
    </div>
  );
}
